import dynamic from 'next/dynamic'
import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
// eslint-disable-next-line import/no-extraneous-dependencies
import { toast } from 'react-toastify'
import cn from 'classnames'
import { useIntl } from 'react-intl'

import { processAlertAction, removeAlertAction } from 'src/comps/AlertMessage/actions'
import { getAlert } from 'src/comps/AlertMessage/selectors'

import Error from 'client/icons/notification/Error'
import Info from 'client/icons/notification/Info'
import Warning from 'client/icons/notification/Warning'
import Success from 'client/icons/notification/Success'
import Default from 'client/icons/notification/Default'
import { type IAlertType } from 'src/comps/AlertMessage'

import styles from './styles.module.css'
import { getAlertContent } from './utils'

const AlertMessageContent = dynamic(() => import('src/comps/Atom/AlertMessageContent'))

export const ALERT_DISPLAY_TIMEOUT = 8000

const alertIcons = {
  info: <Info />,
  success: <Success />,
  warning: <Warning />,
  error: <Error />,
  neutral: <Default />,
}

export enum SeverityType {
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
  NEUTRAL = 'neutral',
}

interface IUseAlertProps {
  containerId?: string
}

export function useAlert({ containerId }: IUseAlertProps = {}) {
  const intl = useIntl()
  const dispatch = useDispatch()
  const alerts = useSelector(getAlert)

  const handleClose = useCallback(
    (alertIndex: number) => dispatch(removeAlertAction(alertIndex)),
    [dispatch]
  )

  useEffect(() => {
    alerts
      .filter(alert => alert.containerId === containerId)
      .forEach((alert, index: number) => {
        if (alert.processed) return

        const severity = alert.severity ?? SeverityType.NEUTRAL
        const alertFn = severity === SeverityType.NEUTRAL ? toast.info : toast[severity]
        const Icon = alertIcons[severity]

        toast.dismiss()
        alertFn(
          (props: { data?: IAlertType }) => {
            const { data } = props
            if (!data) return null
            const alertContent = getAlertContent(intl, data)
            if (!alertContent) return null
            return <AlertMessageContent {...alertContent} />
          },
          {
            data: alert,
            onClose: () => handleClose(index),
            icon: Icon,
            className: cn(styles.alert, styles[`alert-${severity}`]),
            autoClose: ALERT_DISPLAY_TIMEOUT,
            containerId,
          }
        )
        dispatch(processAlertAction(index))
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, alerts, handleClose, containerId])
}
